import { useEffect, useState } from "react";
import { lifted } from "../../boot";
import { getSyncState, onSyncState } from "../../db/client";
import { Editor } from "./Editor";

/*
 * On a phone the note list shows alone at startup, so the first note opened is also the first editor the browser
 * builds: V8 compiles ~1,000 editor functions on the spot (~440 ms of an 840 ms first open at 4× CPU, chat 10).
 * While the list sits idle, this builds one editor offscreen with a little of everything, then drops it, so that
 * work is done before the first tap. Once per session; desktop opens a note at startup and doesn't need it.
 * One step on purpose (chat 19): split in two (an editor with one line, then the sample) it cost more in all and
 * each part still blocked for 150–380 ms at 4× CPU; most of it is TipTap setting up its ~40 extensions the first time.
 */

const SAMPLE = [
  "<h1>Warm up</h1><h2>Section</h2><p>Text with <strong>bold</strong>, <em>italic</em>, <u>underline</u>, <s>strike</s>, ",
  '<mark data-color="#FFEF9E">highlight</mark>, <code>code</code> and <a href="https://example.com">a link</a>.</p>',
  "<ul><li><p>One</p><ul><li><p>Nested</p></li></ul></li></ul><ol><li><p>First</p></li></ol>",
  '<ul data-type="taskList"><li data-type="taskItem" data-checked="false"><p>Checklist</p></li></ul>',
  "<blockquote><p>Quote</p></blockquote><pre><code>code block</code></pre><hr>",
  "<table><tbody><tr><th><p>A</p></th><th><p>B</p></th></tr><tr><td><p>1</p></td><td><p>2</p></td></tr></tbody></table>",
  '<div data-callout="" data-emoji="💡" data-color="yellow"><p>Callout</p></div>',
].join("");

let warmed = false;

export function EditorWarmup() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (warmed) return;
    const idle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 300));
    const cancel = window.cancelIdleCallback ?? clearTimeout;
    let h: number | undefined;
    const start = () =>
      (h = idle(
        () => {
          warmed = true;
          // A note opened first already did the work (and the warm-up would only slow it down).
          if (!document.querySelector(".ProseMirror")) (performance.mark("engram:warmup"), setShow(true));
        },
        // No deadline: only when the page is really idle; a tap first makes it moot.
      ) as number);
    // After the start screen (it waits on fonts and pictures, idle time the warm-up would otherwise take) and, on a
    // new device, after the first download: it holds every query (db worker `firstPull`) and the page sits idle
    // meanwhile; warming up in that wait held the first notes back ~150 ms (chat 19). Later starts have a lastSync.
    let off: (() => void) | undefined;
    let gone = false;
    void lifted.then(() => {
      if (gone) return;
      if (getSyncState().lastSync != null) start();
      else off = onSyncState((s) => s.lastSync != null && h === undefined && (off?.(), start()));
    });
    return () => {
      gone = true;
      off?.();
      if (h !== undefined) cancel(h);
    };
  }, []);
  useEffect(() => {
    if (!show) return;
    // Two frames: the editor and its node views render, then it goes away.
    let f = requestAnimationFrame(() => (f = requestAnimationFrame(() => (performance.mark("engram:warmed"), setShow(false)))));
    return () => cancelAnimationFrame(f);
  }, [show]);
  if (!show) return null;
  return (
    <div className="editor-warmup note-body" aria-hidden="true" inert>
      <Editor noteId="warmup" initialHtml={SAMPLE} editable terms={NO_TERMS} onChange={noop} />
    </div>
  );
}

const NO_TERMS: string[] = [];
const noop = () => {};
